import { Link, useNavigate } from 'react-router-dom';
import { Bell, Wallet, Trophy, User, LogOut, Menu, X, Crown, Tv, Users, Swords, Target, Video, ChevronDown, Settings, Coins, History, Radio } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useFeatures } from '../contexts/FeatureContext';
import { Button } from './ui/button';
import NotificationPopup from './NotificationPopup';
import { BlishAPI } from '@/apis';
import { useStore } from '@/store/useStore';

export const Navbar = () => {
  const user = useStore((state) => state.user);
  const logout = useStore((state) => state.logout);
  const { isEnabled } = useFeatures();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [streamDropdownOpen, setStreamDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [popupNotifications, setPopupNotifications] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const lastNotificationIds = useRef(new Set());
  const pollIntervalRef = useRef(null);
  const dropdownRef = useRef(null);
  const streamDropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await BlishAPI.notifications.getNotifications()
      const allNotifications = res.data;
      const unread = allNotifications.filter(n => !n.read);
      setUnreadCount(unread.length);
      setNotifications(allNotifications);
      
      const newNotifications = unread.filter(n => !lastNotificationIds.current.has(n.id));
      
      if (newNotifications.length > 0) {
        const latestNew = newNotifications[0];
        if (!popupNotifications.find(p => p.id === latestNew.id)) {
          setPopupNotifications(prev => [...prev, latestNew]);
        }
      }
      
      allNotifications.forEach(n => lastNotificationIds.current.add(n.id));
      
    } catch (error) {
      console.error('Failed to fetch notifications', error);
    }
  }, [user, popupNotifications]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchWalletBalance();
      
      pollIntervalRef.current = setInterval(() => {
        fetchNotifications();
        fetchWalletBalance();
      }, 10000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [user, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
      if (streamDropdownRef.current && !streamDropdownRef.current.contains(event.target)) {
        setStreamDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchWalletBalance = async () => {
    if (!user) return;
    try {
      const res = await BlishAPI.wallet.getWallet();
      setWalletBalance(res.data.blish_crowns);
    } catch (error) {
      console.error('Failed to fetch wallet balance');
    }
  };

  const handleClosePopup = (notificationId) => {
    setPopupNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await BlishAPI.notifications.markAsRead(notificationId);
      
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  return (
    <>
      {popupNotifications.map((notification, index) => (
        <div key={notification.id} style={{ top: `${80 + index * 140}px` }}>
          <NotificationPopup
            notification={notification}
            onClose={handleClosePopup}
            onMarkRead={handleMarkRead}
          />
        </div>
      ))}
      
      <nav className="bg-[#1a1a1a]/90 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-logo">
            <Crown className="w-7 h-7 text-lime" style={{filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.6))'}} />
            <span className="text-xl font-bold text-lime" style={{fontFamily: 'Orbitron', textShadow: '0 0 15px rgba(255, 215, 0, 0.5)'}}>BLISHCROWN</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-sm text-gray-300 hover:text-muted-gold transition-colors font-semibold" data-testid="nav-dashboard">
              Dashboard
            </Link>
            {isEnabled('leaderboard') && (
              <Link to="/leaderboard" className="text-sm text-gray-300 hover:text-muted-gold transition-colors flex items-center gap-1 font-semibold" data-testid="nav-leaderboard">
                <Trophy className="w-4 h-4" />Leaderboard
              </Link>
            )}
            {(isEnabled('streaming') || isEnabled('livestream')) && (
              <div className="relative" ref={streamDropdownRef}>
                <button
                  onClick={() => setStreamDropdownOpen(!streamDropdownOpen)}
                  className="text-sm text-gray-300 hover:text-muted-gold transition-colors flex items-center gap-1 font-semibold"
                  data-testid="nav-stream-dropdown"
                >
                  <Radio className="w-4 h-4" />
                  <span>Stream & Live</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${streamDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {streamDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-[#1a1a1a] border border-gray-800 rounded-xl shadow-2xl overflow-hidden z-50">
                    {isEnabled('streaming') && (
                      <Link
                        to="/streams"
                        onClick={() => setStreamDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
                      >
                        <Tv className="w-4 h-4 text-muted-gold" />
                        <span className="text-sm">Watch Streams</span>
                      </Link>
                    )}
                    {isEnabled('livestream') && (
                      <Link
                        to="/livestream"
                        onClick={() => setStreamDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
                      >
                        <Video className="w-4 h-4 text-red-400" />
                        <span className="text-sm">Go Live</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
            {isEnabled('backing') && (
              <Link to="/players" className="text-sm text-gray-300 hover:text-muted-gold transition-colors flex items-center gap-1 font-semibold" data-testid="nav-players">
                <Users className="w-4 h-4" />Back Players
              </Link>
            )}
            {isEnabled('tournaments') && (
              <Link to="/tournaments" className="text-sm text-gray-300 hover:text-muted-gold transition-colors flex items-center gap-1 font-semibold" data-testid="nav-tournaments">
                <Swords className="w-4 h-4" />Tournaments
              </Link>
            )}
            {isEnabled('matchmaking') && (
              <Link to="/matchmaking" className="text-sm text-gray-300 hover:text-muted-gold transition-colors flex items-center gap-1 font-semibold" data-testid="nav-matchmaking">
                <Target className="w-4 h-4" />Matchmaking
              </Link>
            )}
            <Link to="/notifications" className="relative text-sm text-gray-300 hover:text-muted-gold transition-colors" data-testid="nav-notifications">
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center" style={{boxShadow: '0 0 10px rgba(239, 68, 68, 0.8)'}}>{unreadCount}</span>}
            </Link>
            
            {/* User Dropdown Menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-muted-gold transition-colors font-semibold px-3 py-2 rounded-lg hover:bg-gray-800/50"
                data-testid="nav-user-dropdown"
              >
                <div className="w-8 h-8 rounded-full bg-lime flex items-center justify-center">
                  <span className="text-xs font-bold text-[#1a1a1a]">
                    {user?.gamertag?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="max-w-[100px] truncate">{user?.gamertag}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-[#222] border border-gray-800 rounded-xl overflow-hidden z-50">
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-gray-800 bg-lime/5">
                    <p className="text-white font-bold truncate">{user?.gamertag}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>
                  
                  {/* Wallet Balance */}
                  <Link
                    to="/wallet"
                    onClick={() => setUserDropdownOpen(false)}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-800/50 transition-colors border-b border-gray-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-lime/20 flex items-center justify-center">
                        <Coins className="w-5 h-5 text-lime" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Wallet Balance</p>
                        <p className="text-lg font-bold text-lime">
                          {walletBalance !== null ? `${walletBalance.toFixed(0)} BC` : '...'}
                        </p>
                      </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-500 -rotate-90" />
                  </Link>
                  
                  {/* Menu Items */}
                  <div className="py-2">
                    <Link
                      to={`/profile/${user?.id}`}
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm">My Profile</span>
                    </Link>
                    <Link
                      to="/wallet"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
                    >
                      <Wallet className="w-4 h-4" />
                      <span className="text-sm">Wallet</span>
                    </Link>
                    <Link
                      to="/notifications"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
                    >
                      <Bell className="w-4 h-4" />
                      <span className="text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{unreadCount}</span>
                      )}
                    </Link>
                    <Link
                      to={`/profile/${user?.id}`}
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Settings</span>
                    </Link>
                  </div>
                  
                  {/* Logout */}
                  <div className="border-t border-gray-800 py-2">
                    <button
                      onClick={() => { setUserDropdownOpen(false); handleLogout(); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors w-full"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6 text-lime" /> : <Menu className="w-6 h-6 text-lime" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-3 border-t border-gray-800" data-testid="mobile-menu">
            <Link to="/dashboard" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
            {isEnabled('leaderboard') && (
              <Link to="/leaderboard" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Leaderboard</Link>
            )}
            {isEnabled('streaming') && (
              <Link to="/streams" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Streams</Link>
            )}
            {isEnabled('backing') && (
              <Link to="/players" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Back Players</Link>
            )}
            {isEnabled('tournaments') && (
              <Link to="/tournaments" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Tournaments</Link>
            )}
            {isEnabled('matchmaking') && (
              <Link to="/matchmaking" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Matchmaking</Link>
            )}
            {isEnabled('livestream') && (
              <Link to="/livestream" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Live Stream</Link>
            )}
            {(isEnabled('wallet_deposit') || isEnabled('wallet_withdraw')) && (
              <Link to="/wallet" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Wallet</Link>
            )}
            <Link to="/notifications" className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Notifications</Link>
            <Link to={`/profile/${user?.id}`} className="block text-sm text-gray-300 hover:text-muted-gold transition-colors" onClick={() => setMobileMenuOpen(false)}>Profile</Link>
            <button onClick={handleLogout} className="block text-sm text-left w-full text-gray-300 hover:text-red-400 transition-colors">Logout</button>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};
